/**
 * Guest Confirm Page (Public) — invite-a-guest Slice 3.
 * An invited guest lands here from the email link (/guest-confirm/:landingPageId?invite=<token>).
 * Branches on recognized-member:
 *   - Recognized member  → auto-confirm on load (POST, not a side-effecting GET) → thank-you (no zip),
 *                          with a "valued customer" line.
 *   - Not a member       → shows the inviter + read-only name/email + a required zip, then Confirm.
 * Confirming binds the ticket to the invited contact via event-landing-register (bound-invite path).
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { anonSupabase } from "../services/supabase";
import USLSHeader from "../components/landing/USLSHeader";
import USLSFooter from "../components/landing/USLSFooter";
import {
  getRegistrationDefault,
  fillRegistrationTemplate,
  parseRegistrationOffer,
  type RegistrationVariant,
} from "../utils/eventTypeRegistrationDefaults";
import { SmsDisclosureText } from "../components/common/SmsDisclosureText";
import {
  SMS_CONSENT_SURFACES,
  SMS_DISCLOSURE_VERSIONS,
} from "../constants/smsConsentDisclosures";

const anonClient = anonSupabase;

type View = "loading" | "confirm" | "thankyou" | "error";

interface ResolvedInvite {
  ok: boolean;
  reason?: string;
  already_confirmed?: boolean;
  event_title?: string;
  event_type?: string | null;
  event_description?: string | null;
  location_name?: string | null;
  location_address?: Record<string, string> | null;
  start_time?: string | null;
  end_time?: string | null;
  timezone?: string | null;
  guest_first?: string | null;
  guest_last?: string | null;
  guest_email?: string | null;
  inviter_name?: string | null;
  is_member?: boolean;
}

// Full date + time range in the event's timezone, e.g. "Fri, Aug 15, 2026 · 6:00 – 10:00 PM".
function formatWhen(
  startTime?: string | null,
  endTime?: string | null,
  timezone?: string | null,
): string {
  if (!startTime) {
    return "";
  }
  const tz = timezone || "America/Chicago";
  const date = new Date(startTime).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: tz,
  });
  const t: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  };
  const start = new Date(startTime).toLocaleTimeString("en-US", t);
  const end = endTime ? new Date(endTime).toLocaleTimeString("en-US", t) : null;
  return `${date} · ${start}${end ? ` – ${end}` : ""}`;
}

// Time-only range for the copy merge field, e.g. "6:00 PM – 10:00 PM".
function formatTimeRange(
  startTime?: string | null,
  endTime?: string | null,
  timezone?: string | null,
): string | null {
  if (!startTime) {
    return null;
  }
  const tz = timezone || "America/Chicago";
  const t: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  };
  const start = new Date(startTime).toLocaleTimeString("en-US", t);
  const end = endTime ? new Date(endTime).toLocaleTimeString("en-US", t) : null;
  return `${start}${end ? ` – ${end}` : ""}`;
}

function addressToString(
  addr: Record<string, string> | null | undefined,
  locationName?: string | null,
): string {
  const parts = addr
    ? [
        addr.street || addr.address_line1,
        addr.city,
        addr.state,
        addr.postal_code || addr.zip,
      ].filter(Boolean)
    : [];
  const line = parts.join(", ");
  return [locationName, line].filter(Boolean).join(", ") || locationName || "";
}

const REASON_MESSAGES: Record<string, string> = {
  missing_token:
    "This invite link is missing its code. Please use the link from your email.",
  invalid_token:
    "This invite link isn't valid. Please use the most recent link from your email.",
  invalid_page: "We couldn't find this event.",
  revoked:
    "This invite is no longer active. Please contact the person who invited you.",
};

export default function GuestConfirmPage() {
  const { landingPageId } = useParams<{ landingPageId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("invite") || "";

  const [view, setView] = useState<View>("loading");
  const [invite, setInvite] = useState<ResolvedInvite | null>(null);
  // Insurance-state copy variant for locked templates (migration 392); resolved server-side.
  const [regVariant, setRegVariant] = useState<RegistrationVariant>("standard");
  const [zip, setZip] = useState("");
  const [zipError, setZipError] = useState<string | null>(null);
  // Batch #2: non-member guests must provide a phone AND check the required SMS opt-in.
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [smsConsent, setSmsConsent] = useState(false);
  // Server's honest answer: the registration succeeded but the required opt-in was NOT stored.
  const [consentNotRecorded, setConsentNotRecorded] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [wasMember, setWasMember] = useState(false);
  const confirmFired = useRef(false);

  // Submit the confirmation to event-landing-register (bound-invite path binds + issues the ticket).
  // Takes the resolved invite EXPLICITLY (not from state) so the member auto-confirm, which fires
  // immediately after resolve, doesn't read a stale null `invite`.
  const doConfirm = useCallback(
    async (
      res: ResolvedInvite,
      postalCode?: string,
      guestPhone?: string,
      messagingConsent?: boolean,
    ) => {
      if (!landingPageId || !res.ok) {
        return;
      }
      setSubmitting(true);
      setErrorMsg(null);
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const resp = await fetch(
          `${supabaseUrl}/functions/v1/event-landing-register`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              landingPageId,
              inviteToken: token,
              // Identity is bound to the invited contact server-side; these satisfy the required-field
              // check and are otherwise ignored (bound path drops submitted identity).
              firstName: res.guest_first || "Guest",
              lastName: res.guest_last || "Guest",
              email: res.guest_email || "guest@example.com",
              ...(postalCode ? { postalCode } : {}),
              // Batch #2: forward the non-member guest's phone + affirmative SMS consent so the
              // edge fn records opt-in on the bound contact. Omitted on the member auto-confirm path.
              ...(guestPhone ? { phone: guestPhone } : {}),
              // `consentSurface` tells the server WHICH disclosure this page displayed, so it can
              // freeze that exact wording into the evidence row. The client never supplies the
              // wording itself — it names the surface and the server resolves the text.
              ...(messagingConsent
                ? {
                    messagingConsent: true,
                    consentSurface: SMS_CONSENT_SURFACES.guestConfirm,
                  }
                : {}),
            }),
          },
        );
        // 409 = already registered → treat as success (idempotent: prefetch/reload/double-submit).
        if (resp.ok || resp.status === 409) {
          // Round-2 audit MED: a 2xx means the SPOT is confirmed — it does NOT mean the required
          // text-message opt-in was captured. Read the server's honest flag so the page never
          // silently implies consent was recorded when it was not.
          if (messagingConsent) {
            const data = await resp
              .json()
              .catch(() => ({}) as { consentRecorded?: boolean });
            setConsentNotRecorded(
              (data as { consentRecorded?: boolean }).consentRecorded === false,
            );
          }
          setView("thankyou");
        } else {
          const data = await resp.json().catch(() => ({}));
          setErrorMsg(
            data.error || "We couldn't confirm your spot. Please try again.",
          );
          setView("error");
        }
      } catch {
        setErrorMsg("We couldn't reach the server. Please try again.");
        setView("error");
      } finally {
        setSubmitting(false);
      }
    },
    [landingPageId, token],
  );

  // Resolve the invite on load; auto-confirm recognized members.
  useEffect(() => {
    let cancelled = false;
    async function resolve() {
      if (!landingPageId || !token) {
        setErrorMsg(REASON_MESSAGES.missing_token);
        setView("error");
        return;
      }
      try {
        const { data, error } = await anonClient.rpc("resolve_guest_invite", {
          p_landing_page_id: landingPageId,
          p_token: token,
        });
        if (cancelled) {
          return;
        }
        const res = (data as ResolvedInvite | null) ?? null;
        if (error || !res || !res.ok) {
          setErrorMsg(
            REASON_MESSAGES[res?.reason || ""] ||
              "This invite link isn't valid.",
          );
          setView("error");
          return;
        }
        // Resolve the copy variant BEFORE showing any content, so a guest never sees the wrong copy.
        if (getRegistrationDefault(res.event_type)?.locked) {
          try {
            // Mig 1480: the offer (the event's own amount, the state's copy) or the variant.
            const { data: offer, error: vErr } = await anonClient.rpc(
              "eib_registration_offer",
              { p_landing_page_id: landingPageId },
            );
            // Fail toward the insurance copy: it only omits the Bonus Offer paragraph.
            setRegVariant(vErr ? "insurance" : parseRegistrationOffer(offer));
          } catch {
            setRegVariant("insurance");
          }
          if (cancelled) {
            return;
          }
        }
        setInvite(res);
        setWasMember(!!res.is_member);
        if (res.already_confirmed) {
          setView("thankyou");
          return;
        }
        if (res.is_member) {
          // Recognized member → auto-confirm (POST on load; scanner-prefetch is a GET so it won't fire this).
          if (!confirmFired.current) {
            confirmFired.current = true;
            void doConfirm(res);
          }
        } else {
          setView("confirm");
        }
      } catch {
        if (!cancelled) {
          setErrorMsg("We couldn't load your invite. Please try again.");
          setView("error");
        }
      }
    }
    resolve();
    return () => {
      cancelled = true;
    };
    // doConfirm intentionally omitted — resolve runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingPageId, token]);

  const handleConfirm = () => {
    const z = zip.trim();
    if (!z) {
      setZipError("ZIP code is required");
      return;
    }
    if (!/^\d{5}(-\d{4})?$/.test(z)) {
      setZipError("Enter a valid ZIP (12345 or 12345-6789)");
      return;
    }
    setZipError(null);

    // Batch #2: phone is required, and the SMS opt-in must be checked, before a non-member
    // guest can confirm. (Members never reach this form — they auto-confirm on load.)
    const p = phone.trim();
    if (!p) {
      setPhoneError("Phone number is required");
      return;
    }
    // Require at least 10 digits so an obviously-incomplete number can't be captured as consent.
    if ((p.match(/\d/g) || []).length < 10) {
      setPhoneError("Enter a valid phone number");
      return;
    }
    setPhoneError(null);

    if (!smsConsent) {
      setConsentError("Please check the box to agree to receive text messages");
      return;
    }
    setConsentError(null);

    if (invite) {
      void doConfirm(invite, z, p, true);
    }
  };

  const guestName = invite
    ? `${invite.guest_first || ""} ${invite.guest_last || ""}`.trim()
    : "";

  // Event context so the guest — who never saw the registration page — gets a clear, enticing view.
  const regDefault = getRegistrationDefault(invite?.event_type, regVariant);
  const whenText = formatWhen(
    invite?.start_time,
    invite?.end_time,
    invite?.timezone,
  );
  const locationText = addressToString(
    invite?.location_address,
    invite?.location_name,
  );
  const mapSrc = locationText
    ? `https://maps.google.com/maps?q=${encodeURIComponent(locationText)}&z=14&output=embed`
    : null;
  // Locked-template event types (Range Night) show the same bundled description the member saw,
  // with the venue + time merged in.
  const detailsHtml =
    regDefault?.locked && invite
      ? DOMPurify.sanitize(
          fillRegistrationTemplate(regDefault.eventDetailsTemplate, {
            eventTime: formatTimeRange(
              invite.start_time,
              invite.end_time,
              invite.timezone,
            ),
            orgName: invite.location_name,
          }),
          { FORBID_TAGS: ["script", "iframe", "object", "embed", "form"] },
        )
      : // Other event types: show the event's own description so every guest gets context.
        invite?.event_description
        ? DOMPurify.sanitize(invite.event_description, {
            FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
          })
        : "";

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <USLSHeader />
      <main className="flex-1 flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-2xl">
          {view === "loading" && (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C8102E] mx-auto" />
              <p className="text-gray-500 mt-4 text-sm">Loading your invite…</p>
            </div>
          )}

          {view === "confirm" && invite && (
            <div className="space-y-6">
              {/* Banner (locked-template event types, e.g. Range Night) */}
              {regDefault?.bannerImageUrl && (
                <img
                  src={regDefault.bannerImageUrl}
                  alt={regDefault.bannerAlt}
                  className="w-full h-auto rounded-lg"
                />
              )}

              {/* You're invited */}
              <div>
                <h1 className="text-2xl font-bold text-[#1B2A4A] mb-2">
                  You&rsquo;re invited!
                </h1>
                <p className="text-gray-700">
                  {invite.inviter_name ? (
                    <>
                      <strong>{invite.inviter_name}</strong> invited you to{" "}
                    </>
                  ) : (
                    <>You&rsquo;ve been invited to </>
                  )}
                  <strong>{invite.event_title}</strong>.
                </p>
              </div>

              {/* When & Where + map */}
              {(whenText || locationText) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h2 className="text-sm font-semibold text-[#1B2A4A] uppercase mb-2">
                      When &amp; Where
                    </h2>
                    {whenText && (
                      <p className="text-sm text-gray-700">{whenText}</p>
                    )}
                    {invite.location_name && (
                      <p className="text-sm text-gray-700 mt-1">
                        {invite.location_name}
                      </p>
                    )}
                  </div>
                  {mapSrc && (
                    <div className="rounded-lg overflow-hidden border border-gray-200">
                      <iframe
                        title="Event location map"
                        src={mapSrc}
                        className="w-full h-40"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Event description */}
              {detailsHtml && (
                <div
                  className="border border-gray-200 rounded-lg p-4 prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: detailsHtml }}
                />
              )}

              {/* Confirm */}
              <div className="border border-gray-200 rounded-lg p-6">
                <p className="text-gray-700 mb-4 text-sm">
                  Confirm below and we&rsquo;ll send your ticket.
                </p>
                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                  <div className="text-gray-500">Registering as</div>
                  <div className="font-medium text-[#1B2A4A]">{guestName}</div>
                  <div className="text-gray-600">{invite.guest_email}</div>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP code <span className="text-[#C8102E]">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="ZIP code"
                  className={`w-full px-3 py-2 border rounded text-base focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${zipError ? "border-red-300" : "border-gray-300"}`}
                />
                {zipError && (
                  <p className="text-xs text-red-500 mt-1">{zipError}</p>
                )}

                <label className="block text-sm font-medium text-gray-700 mb-1 mt-4">
                  Phone number <span className="text-[#C8102E]">*</span>
                </label>
                <input
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className={`w-full px-3 py-2 border rounded text-base focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${phoneError ? "border-red-300" : "border-gray-300"}`}
                />
                {phoneError && (
                  <p className="text-xs text-red-500 mt-1">{phoneError}</p>
                )}

                {/* Required SMS consent (batch #2). Wording is a placeholder to be finalized by Legal. */}
                <label className="mt-4 flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-[#C8102E] focus:ring-[#C8102E]"
                  />
                  <span className="text-xs text-gray-600">
                    {/* Rendered from the canonical registry so the words shown here stay
                        byte-identical to the disclosure frozen into the consent evidence row. */}
                    <SmsDisclosureText
                      version={SMS_DISCLOSURE_VERSIONS.guestConfirm}
                    />{" "}
                    <span className="text-[#C8102E]">*</span>
                  </span>
                </label>
                {consentError && (
                  <p className="text-xs text-red-500 mt-1">{consentError}</p>
                )}

                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={submitting}
                  className="mt-5 w-full px-6 py-3 bg-[#C8102E] text-white font-semibold rounded hover:bg-[#a50d26] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? "Confirming…" : "Register & Confirm"}
                </button>
              </div>
            </div>
          )}

          {view === "thankyou" && (
            <div className="text-center py-10">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-[#1B2A4A] mb-3">
                Thank you!
              </h1>
              {consentNotRecorded && (
                /* The spot is confirmed, but the text-message opt-in was not stored. Say so rather
                   than letting the success screen imply we captured it. */
                <p className="mx-auto mb-3 max-w-sm rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                  Your spot is confirmed, but we couldn&rsquo;t record your
                  text-message preference. You are <strong>not</strong> signed
                  up for texts. Our team has been notified.
                </p>
              )}
              <p className="text-gray-700 mb-2">Your ticket is on the way.</p>
              {wasMember && (
                <p className="text-gray-600 text-sm mt-3">
                  Thank you for being a valued U.S. LawShield customer.
                </p>
              )}
            </div>
          )}

          {view === "error" && (
            <div className="text-center py-12">
              <h1 className="text-xl font-bold text-[#1B2A4A] mb-3">
                We hit a snag
              </h1>
              <p className="text-gray-600 mb-5">{errorMsg}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-5 py-2.5 bg-[#1B2A4A] text-white font-medium rounded hover:bg-[#2a3d66] transition-colors text-sm"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </main>
      <USLSFooter />
    </div>
  );
}
